import React, { CSSProperties, useState } from "react";
import { PhoneInput, CountryIso2 } from "react-international-phone";
import "react-international-phone/style.css";
import { hasActualPhoneNumber } from "../utils/phoneUtils";

/**
 * Single source of truth for the default dial country across the app.
 * Palestine. Override per-field via the `defaultCountry` prop only when a
 * specific form genuinely needs a different default.
 */
export const DEFAULT_PHONE_COUNTRY: CountryIso2 = "ps";

interface PhoneNumberInputProps {
    /** Phone value in E.164 format (e.g. "+970591234567"). */
    value?: string | null;
    /** Fires with the new E.164 value on every change. */
    onChange: (value: string) => void;
    disabled?: boolean;
    required?: boolean;
    /** Used in the "<label> is required" validation message. */
    label?: string;
    /** ISO2 country used to seed the dial code. Defaults to Palestine. */
    defaultCountry?: CountryIso2;
    /**
     * When false (default) the dial code is prefilled into an empty field so
     * the user sees the country code. A field left with only that dial code is
     * treated as empty and cleared on blur (see handleBlur).
     */
    disableDialCodePrefill?: boolean;
    className?: string;
    inputClassName?: string;
    style?: CSSProperties;
}

/**
 * Shared phone-number input — wraps react-international-phone's PhoneInput.
 * Centralises the default country, the one-time stylesheet import, dial-code
 * prefill, and two behaviours the bare library lacks:
 *   1. On blur, a value that is only a dial code (the prefill, with no real
 *      number) is stripped to "" so a bare "+970" is never persisted.
 *   2. A `required` field with no actual number shows a validation message.
 * Use this everywhere a phone number is ENTERED (not for search filters,
 * which take plain text fragments).
 */
const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
    value,
    onChange,
    disabled,
    required,
    label = "Phone number",
    defaultCountry = DEFAULT_PHONE_COUNTRY,
    disableDialCodePrefill = false,
    className,
    inputClassName,
    style,
}) => {
    const [error, setError] = useState<string | undefined>(undefined);

    const handleBlur = () => {
        const hasNumber = hasActualPhoneNumber(value);
        // Don't persist a bare dial code left by the prefill (e.g. "+970"):
        // clear it back to empty when no real number was entered. The prefill
        // still re-shows the dial code on the next focus.
        if (!hasNumber && value) {
            onChange("");
        }
        setError(required && !hasNumber ? `${label} is required` : undefined);
    };

    return (
        <div>
            <PhoneInput
                defaultCountry={defaultCountry}
                value={value ?? ""}
                onChange={(phone) => {
                    onChange(phone);
                    if (error) setError(undefined);
                }}
                onBlur={handleBlur}
                disabled={disabled}
                disableDialCodePrefill={disableDialCodePrefill}
                className={className}
                inputClassName={inputClassName}
                inputStyle={error ? { borderColor: "var(--field-invalid)" } : undefined}
                style={style}
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
        </div>
    );
};

export default PhoneNumberInput;
